<script setup lang="ts">
import {cva} from 'class-variance-authority'

import Blobs from '~/assets/icons/small-blobs-light.svg?component'

import {useNav} from './nav'

const nav = useNav()
const localePath = useLocalePath()


const NuxtLink = resolveComponent('NuxtLink')


const navItemClass = cva([
  'font-medium text-sm text-gray-900 transition h-10 flex items-center px-3 rounded-lg border border-transparent',
  'hover:border-main-100 hover:text-main-900 hover:bg-main-50',
])
</script>

<template>
  <div class="flex flex-row items-center space-x-8">
    <NavigationMenuRoot class="relative flex flex-row items-center justify-center">
      <NavigationMenuList class="flex items-center space-x-1">
        <NavigationMenuItem v-for="item of nav" :key="item.label" class="flex h-10 items-center">
          <template v-if="item.type === 'internal'">
            <NavigationMenuLink as-child class="h-full">
              <NuxtLink :to="item.to" :class="navItemClass()">
                {{ item.label }}
              </NuxtLink>
            </NavigationMenuLink>
          </template>

          <template v-else-if="item.type === 'subnav' || item.type === 'subnav-work'">
            <NavigationMenuTrigger :class="navItemClass()">
              {{ item.label }}
            </NavigationMenuTrigger>

            <NavigationMenuContent class="absolute left-0 top-0 w-auto">
              <div
                class="flex space-x-3 rounded-lg p-6"
                :class="[
                  {'w-[496px]': item.type !== 'subnav-work'},
                  {'w-[496px]': item.type === 'subnav-work' && item.links.length > 5},
                  {'w-[250px]': item.type === 'subnav-work' && item.links.length <= 5},
                ]"
              >
                <NavigationMenuLink v-if="item.highlight" as-child>
                  <NuxtLink
                    v-if="item.highlight"
                    :to="item.highlight.type === 'internal' ? item.highlight.to : ''"
                    class="group relative flex w-1/2 flex-col justify-end overflow-hidden rounded p-4 pt-24 text-sm bordershadow-main"
                  >
                    <Blobs
                      class="absolute inset-0 z-0 h-full w-full transform opacity-25 transition group-hover:scale-105 group-hover:opacity-50"
                    />
                    <Heading size="small" class="relative text-main-950">
                      {{ item.highlight.label }}
                    </Heading>
                    <Paragraph size="small" class="relative text-gray-800">
                      {{ item.highlight.body }}
                    </Paragraph>
                  </NuxtLink>
                </NavigationMenuLink>

                <div :class="[{'w-1/2': item.highlight}, {'flex flex-col': item.type !== 'subnav-work'}]">
                  <NavigationMenuLink
                    v-for="sublink of item.links.filter(l => l.label !== 'Logistics')"
                    :key="sublink.label"
                    as-child
                  >
                    <NuxtLink
                      v-if="sublink.type === 'internal'"
                      :to="sublink.to"
                      class="flex items-center space-x-2 rounded px-4 py-3 transition hover:bg-gray-100"
                    >
                      <Paragraph size="small" class="font-medium text-gray-600">
                        {{ sublink.label }}
                      </Paragraph>
                    </NuxtLink>
                  </NavigationMenuLink>
                </div>
              </div>
            </NavigationMenuContent>
          </template>
        </NavigationMenuItem>

        <div class="w-2"></div>

        <Action intent="main" size="sm" as="link" :to="localePath('/contact')">
          {{ $t('header.nav.contact') }}
        </Action>
      </NavigationMenuList>

      <div class="absolute right-0 top-full flex min-w-full justify-center perspective-[2000px]">
        <NavigationMenuViewport
          :class="[
            'relative mt-2 overflow-hidden rounded-lg bg-white bordershadow-lg-gray',
            'origin-[top_center] transition-[width,_height] duration-300',
            'data-[state=closed]:animate-scaleOut data-[state=open]:animate-scaleIn',
            'h-[var(--radix-navigation-menu-viewport-height)]',
            'sm:w-[var(--radix-navigation-menu-viewport-width)]',
          ]"
        />
      </div>
    </NavigationMenuRoot>
  </div>
</template>
